import { useEffect } from "react";
import { useCanEdit, useWorkspace } from "@/api/session";
import { useUpdateWorkspace } from "@/api/workspace";
import { useToast } from "@/components";
import { useTheme } from "./ThemeProvider";
import { isThemeChoice, type ThemeChoice } from "./themes";

/**
 * Theme switching for the user menu, Settings and the palette: applies at once, remembers the
 * choice on this device (localStorage) and in the workspace (settings.theme) for other devices.
 */
export function useThemeSwitcher() {
  const { choice, theme, setChoice } = useTheme();
  const canEdit = useCanEdit();
  const updateWorkspace = useUpdateWorkspace();
  const toast = useToast();

  function choose(next: ThemeChoice) {
    setChoice(next);
    if (!canEdit) return;
    updateWorkspace.mutate(
      { settings: { theme: next } },
      {
        onError: () =>
          toast.error("Theme saved on this device only", {
            description: "The workspace setting couldn't be updated, so other devices keep their theme.",
          }),
      },
    );
  }

  return { choice, theme, choose };
}

/**
 * On a device where no theme was chosen yet, adopt the workspace's theme. The backend default is
 * "light", which can't be told apart from "never chosen", so only dark, paper and system are
 * adopted; otherwise the device keeps following prefers-color-scheme.
 */
export function useAdoptWorkspaceTheme(): void {
  const { chosenOnThisDevice, adoptChoice } = useTheme();
  const workspaceTheme = useWorkspace()?.settings.theme;
  useEffect(() => {
    if (chosenOnThisDevice || !isThemeChoice(workspaceTheme) || workspaceTheme === "light") return;
    adoptChoice(workspaceTheme);
  }, [chosenOnThisDevice, workspaceTheme, adoptChoice]);
}
